import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export type Persona = 'lawyer' | 'agent' | 'client' | 'superadmin';

interface PersonaContextType {
  persona: Persona;
  personaLabel: string;
  personaColor: string;
}

const PersonaContext = createContext<PersonaContextType | undefined>(undefined);

const personaLabels: Record<Persona, string> = {
  lawyer: 'Lawyer',
  agent: 'Real Estate Agent',
  client: 'Client',
  superadmin: 'Super Admin',
};

const personaColors: Record<Persona, string> = {
  lawyer: 'bg-lawyer text-lawyer-foreground',
  agent: 'bg-agent text-agent-foreground',
  client: 'bg-client text-client-foreground',
  superadmin: 'bg-primary text-primary-foreground',
};

export function PersonaProvider({ children }: { children: ReactNode }) {
  const { profile, isSuperAdmin } = useAuth();
  const persona: Persona = isSuperAdmin ? 'superadmin' : (profile?.role as Persona) || 'client';

  const value: PersonaContextType = {
    persona,
    personaLabel: personaLabels[persona],
    personaColor: personaColors[persona],
  };

  return (
    <PersonaContext.Provider value={value}>
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona() {
  const context = useContext(PersonaContext);
  if (context === undefined) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
}
